import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { StepGuide, SCorpGuide, ModuleTips } from '../components/onboarding';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Lightbulb, TrendingUp, Building2, PiggyBank, Receipt, Car,
  Calendar, AlertTriangle, CheckCircle2, Loader2, ChevronRight,
  DollarSign, Clock, Target, Sparkles, ArrowRight, Bell, MessageCircle,
  Trash2, Download, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Strategy = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [financialSummary, setFinancialSummary] = useState({});
  const [totalSavings, setTotalSavings] = useState(0);
  const [deadlines, setDeadlines] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisGeneratedAt, setAnalysisGeneratedAt] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearingAnalysis, setClearingAnalysis] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState('recommendations');
  
  // Reminder dialog state
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [reminderDays, setReminderDays] = useState(7);
  const [userReminders, setUserReminders] = useState([]);

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchRecommendations();
      fetchDeadlines();
      fetchSavedAnalysis(selectedYear);
    }
  }, [selectedYear]);

  // Handle navigation from dashboard with specific strategy
  useEffect(() => {
    if (location.state?.strategyId) {
      setActiveTab('recommendations');
    }
    if (location.state?.showDeadlines) {
      setActiveTab('deadlines');
    }
  }, [location.state]);

  const fetchYears = async () => {
    try {
      const response = await axios.get(`${API}/transactions/years`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      const currentYear = new Date().getFullYear();
      const sevenYears = Array.from({ length: 7 }, (_, i) => currentYear - i);
      const allYears = [...new Set([...sevenYears, ...(response.data || [])])].sort((a, b) => b - a);
      setAvailableYears(allYears);
    } catch (error) {
      const currentYear = new Date().getFullYear();
      setAvailableYears(Array.from({ length: 7 }, (_, i) => currentYear - i));
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/strategy/recommendations?year=${selectedYear}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRecommendations(response.data.recommendations || []);
      setFinancialSummary(response.data.financial_summary || {});
      // ── Commandment VIII: no fallback — surface missing field explicitly ──
      setTotalSavings(response.data.total_potential_savings ?? null);
    } catch (error) {
      toast.error(t('toast.failed_to_load_recommendations'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDeadlines = async () => {
    try {
      const response = await axios.get(`${API}/strategy/tax-calendar?year=${selectedYear}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setDeadlines(response.data.deadlines || []);
      
      // Fetch user's saved reminders
      try {
        const remindersRes = await axios.get(`${API}/user/reminders`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setUserReminders(remindersRes.data || []);
      } catch (e) {
        // Reminders endpoint may not exist yet
        setUserReminders([]);
      }
    } catch (error) {
      // Tax deadlines will show empty
    }
  };

  const getAiAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/strategy/ai-analysis`, 
        { focus_area: 'general', year: selectedYear },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setAiAnalysis(response.data.analysis);
      setAnalysisGeneratedAt(response.data.generated_at);
      toast.success(t('toast.ai_analysis_complete'));
    } catch (error) {
      toast.error(t('toast.failed_to_get_ai_analysis'));
    } finally {
      setAnalyzing(false);
    }
  };

  const fetchSavedAnalysis = async (year) => {
    setAiAnalysis(null);
    setAnalysisGeneratedAt(null);
    try {
      const res = await axios.get(`${API}/strategy/analysis/${year}`, {
        headers: getAuthHeaders(), withCredentials: true
      });
      setAiAnalysis(res.data.analysis);
      setAnalysisGeneratedAt(res.data.generated_at);
    } catch {
      // 404 = no saved analysis for this year — that's fine, show "Run" button
    }
  };

  const handleDownloadAnalysisPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/strategy/analysis/${selectedYear}/pdf`, {
        headers: getAuthHeaders(),
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SolelyTax_TaxStrategy_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Strategy analysis downloaded!');
    } catch {
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleClearAnalysis = async (downloadFirst = false) => {
    if (downloadFirst) {
      await handleDownloadAnalysisPdf();
    }
    setClearingAnalysis(true);
    try {
      await axios.delete(`${API}/strategy/analysis/${selectedYear}`, {
        headers: getAuthHeaders(), withCredentials: true
      });
      setAiAnalysis(null);
      setAnalysisGeneratedAt(null);
      setShowClearDialog(false);
      toast.success(`${selectedYear} analysis cleared.`);
    } catch {
      toast.error('Failed to clear analysis.');
    } finally {
      setClearingAnalysis(false);
    }
  };

  const handleSetReminder = async () => {
    if (!selectedDeadline) return;
    
    try {
      await axios.post(`${API}/user/reminders`, {
        type: 'tax_deadline',
        title: selectedDeadline.title,
        description: selectedDeadline.description,
        due_date: selectedDeadline.date,
        remind_days_before: reminderDays
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success(t('toast.reminder_set_successfully'));
      setReminderDialogOpen(false);
      setSelectedDeadline(null);
      fetchDeadlines(); // Refresh to show reminder status
    } catch (error) {
      toast.error(t('toast.failed_to_set_reminder'));
    }
  };

  const openReminderDialog = (deadline) => {
    setSelectedDeadline(deadline);
    setReminderDays(deadline.days_until > 7 ? 7 : Math.max(1, deadline.days_until - 1));
    setReminderDialogOpen(true);
  };

  const askAiAboutStrategy = (strategy) => {
    // Commandment VIII: never conflate missing net_income with zero — omit the field if absent
    const netIncomeClause = financialSummary.net_income != null
      ? `Net income is $${financialSummary.net_income.toLocaleString()} for ${selectedYear}. `
      : '';
    navigate('/chat', {
      state: {
        prefillMessage: `Tell me more about "${strategy.title}" strategy. My current situation: ${netIncomeClause}${strategy.description}`
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      entity_structure: Building2,
      retirement: PiggyBank,
      deductions: Receipt,
      tax_reduction: DollarSign,
      vehicle: Car
    };
    return icons[category] || Lightbulb;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      hard: 'bg-red-100 text-red-700'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-700';
  };

  const hasReminder = (deadline) => {
    return userReminders.some(r => r.title === deadline.title);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="strategy-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
              <Target className="w-8 h-8 text-amber-500" />
              {t('strategy.title', 'Strategy Recommendations')}
            </h1>
            <p className="text-muted-foreground">{t('strategy.subtitle', 'AI-powered tax strategies personalized for your business')}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="year-select">
                <SelectValue placeholder={t('common.year', 'Year')} />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={getAiAnalysis} disabled={analyzing} className="bg-gradient-to-r from-purple-600 to-blue-600">
              {analyzing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('strategy.analyzing', 'Analyzing...')}</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />{t('strategy.get_ai_analysis', 'Get AI Analysis')}</>
              )}
            </Button>
          </div>
        </div>

        {/* S-Corp Payroll Guide - Only shown for S-Corp users */}
        <SCorpGuide className="mb-4" />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">{t('strategy.net_income_year', 'Net Income')} ({selectedYear})</p>
                  <p className="text-2xl font-bold">{formatCurrency(financialSummary.net_income)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">{t('strategy.potential_savings', 'Potential Savings')}</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalSavings)}</p>
                </div>
                <PiggyBank className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">{t('strategy.current_est_tax', 'Current Est. Tax')}</p>
                  <p className="text-2xl font-bold">{formatCurrency(financialSummary.current_estimated_tax)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="recommendations">{t('strategy.recommendations', 'Recommendations')}</TabsTrigger>
            <TabsTrigger value="deadlines">{t('strategy.tax_calendar', 'Tax Calendar')}</TabsTrigger>
            <TabsTrigger value="ai-insights">{t('strategy.ai_insights', 'AI Insights')}</TabsTrigger>
          </TabsList>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-4">
            {recommendations.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Lightbulb className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('strategy.add_transactions_prompt', 'Add transactions for {{year}} to get personalized recommendations', { year: selectedYear })}</p>
                </CardContent>
              </Card>
            ) : (
              recommendations.map((rec, idx) => {
                const Icon = getCategoryIcon(rec.category);
                return (
                  <Card key={rec.id || idx} className="hover:shadow-md transition-shadow" data-testid={`strategy-${rec.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          rec.priority === 'high' ? 'bg-emerald-100' : 'bg-amber-100'
                        }`}>
                          <Icon className={`w-6 h-6 ${rec.priority === 'high' ? 'text-emerald-600' : 'text-amber-600'}`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-lg">{rec.title}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={rec.priority === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                  {rec.priority} priority
                                </Badge>
                                {rec.difficulty && (
                                  <Badge className={getDifficultyColor(rec.difficulty)}>
                                    {rec.difficulty}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {rec.potential_savings > 0 && (
                              <div className="text-right">
                                <p className="text-sm text-muted-foreground">Potential Savings</p>
                                <p className="text-xl font-bold text-emerald-600">{formatCurrency(rec.potential_savings)}</p>
                              </div>
                            )}
                          </div>
                          
                          <p className="text-muted-foreground mb-4">{rec.description}</p>
                          
                          <div className="flex flex-wrap gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => askAiAboutStrategy(rec)}
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Ask AI Advisor
                            </Button>
                            {rec.action && (
                              <Badge variant="secondary" className="py-1.5">
                                <ArrowRight className="w-3 h-3 mr-1" />
                                {rec.action}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Tax Calendar Tab */}
          <TabsContent value="deadlines" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Upcoming Tax Deadlines
                </CardTitle>
                <CardDescription>Set reminders to stay on track with important dates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {deadlines.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No upcoming deadlines</p>
                  </div>
                ) : (
                  deadlines.map((deadline, idx) => (
                    <div 
                      key={deadline.id || deadline.date || `deadline-${idx}`} 
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        deadline.urgency === 'urgent' ? 'bg-red-50 border-red-200' :
                        deadline.urgency === 'soon' ? 'bg-amber-50 border-amber-200' :
                        'bg-muted/30'
                      }`}
                      data-testid={`deadline-${idx}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          deadline.urgency === 'urgent' ? 'bg-red-100' :
                          deadline.urgency === 'soon' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          <Calendar className={`w-5 h-5 ${
                            deadline.urgency === 'urgent' ? 'text-red-600' :
                            deadline.urgency === 'soon' ? 'text-amber-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium">{deadline.title}</p>
                          <p className="text-sm text-muted-foreground">{deadline.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold">{deadline.date}</p>
                          <p className={`text-sm ${
                            deadline.urgency === 'urgent' ? 'text-red-600' :
                            deadline.urgency === 'soon' ? 'text-amber-600' : 'text-muted-foreground'
                          }`}>
                            {deadline.days_until === 0 ? 'Today!' : `${deadline.days_until} days`}
                          </p>
                        </div>
                        <Button
                          variant={hasReminder(deadline) ? "secondary" : "outline"}
                          size="sm"
                          onClick={() => openReminderDialog(deadline)}
                          disabled={hasReminder(deadline)}
                          data-testid={`set-reminder-${idx}`}
                        >
                          <Bell className="w-4 h-4" />
                          {hasReminder(deadline) ? 'Reminder Set' : ''}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Insights Tab */}
          <TabsContent value="ai-insights" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  AI Tax Analysis
                </CardTitle>
                <CardDescription>
                  Personalized insights for {selectedYear} powered by AI based on your financial data
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!aiAnalysis ? (
                  <div className="text-center py-12">
                    <Sparkles className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">
                      Get a detailed AI-powered analysis of your {selectedYear} tax situation with specific recommendations.
                    </p>
                    <Button onClick={getAiAnalysis} disabled={analyzing} data-testid="generate-analysis-btn">
                      {analyzing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" />Generate {selectedYear} Analysis</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none">
                    {/* Timestamp + action buttons */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      {analysisGeneratedAt && (
                        <Badge variant="outline" className="text-xs text-stone-500 gap-1" data-testid="analysis-timestamp">
                          <Clock className="w-3 h-3" />
                          Generated {new Date(analysisGeneratedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Badge>
                      )}
                      <div className="flex gap-2 ml-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadAnalysisPdf}
                          disabled={downloadingPdf}
                          data-testid="download-analysis-btn"
                        >
                          {downloadingPdf
                            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating...</>
                            : <><Download className="w-3.5 h-3.5 mr-1.5" />Download PDF</>}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={getAiAnalysis}
                          disabled={analyzing}
                          data-testid="regenerate-analysis-btn"
                        >
                          {analyzing
                            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Analyzing...</>
                            : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Re-run</>}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => setShowClearDialog(true)}
                          data-testid="clear-analysis-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />Clear
                        </Button>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 whitespace-pre-wrap">
                      {aiAnalysis}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Clear Analysis Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="sm:max-w-md" data-testid="clear-analysis-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Clear {selectedYear} Analysis?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete your saved {selectedYear} tax strategy analysis.
              You can re-run it at any time, but <strong>the current results will be gone and cannot be recovered</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <strong>Tip:</strong> Download the PDF first to keep a copy before clearing.
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowClearDialog(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleClearAnalysis(true)}
              disabled={clearingAnalysis || downloadingPdf}
              className="w-full sm:w-auto border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="download-then-clear-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF, then Clear
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleClearAnalysis(false)}
              disabled={clearingAnalysis}
              className="w-full sm:w-auto"
              data-testid="confirm-clear-btn"
            >
              {clearingAnalysis
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Clearing...</>
                : <>Clear Without Saving</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Set Reminder
            </DialogTitle>
            <DialogDescription>
              {selectedDeadline?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Remind me</Label>
              <Select value={reminderDays.toString()} onValueChange={(v) => setReminderDays(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select days before" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day before</SelectItem>
                  <SelectItem value="3">3 days before</SelectItem>
                  <SelectItem value="7">7 days before</SelectItem>
                  <SelectItem value="14">14 days before</SelectItem>
                  <SelectItem value="30">30 days before</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-muted-foreground">
                <strong>Deadline:</strong> {selectedDeadline?.date}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Reminder:</strong> {reminderDays} days before
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReminderDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSetReminder}>
                <Bell className="w-4 h-4 mr-2" />
                Set Reminder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    <ModuleTips moduleId="strategy" />
  </>
  );
};

export default Strategy;
